class CollectionMenuController extends HTMLElement {
  constructor () {
    super();
    this._onClick = this._onClick.bind(this);
    this._onHashMaybeActivate = this._onHashMaybeActivate.bind(this);
    this._lastTarget = null;
    this._suppressScrollOnce = false;
  }

  connectedCallback () {
    this._markAnchorsDisableGlobalScroll();
    this.addEventListener('click', this._onClick, { capture: true });
    window.addEventListener('hashchange', this._onHashMaybeActivate, { passive: true });
    requestAnimationFrame(() => this._onHashMaybeActivate());
  }

  disconnectedCallback () {
    this.removeEventListener('click', this._onClick, { capture: true });
    window.removeEventListener('hashchange', this._onHashMaybeActivate);
  }

  get activeClass () {
    return this.getAttribute('active-class') || 'active';
  }

  get linkActiveClass () {
    return 'collection-hero-horizontal-menu__item--active';
  }

  _markAnchorsDisableGlobalScroll () {
    this.querySelectorAll('a[href*="#"]').forEach(a => {
      if (!a.hasAttribute('data-disabled-scroll-to')) {
        a.setAttribute('data-disabled-scroll-to', 'true');
      }
    });
  }

  _getScrollOffsetPx () {
    const cs = getComputedStyle(document.documentElement);
    const varVal = cs.getPropertyValue('--header-height').trim();
    let px = 0;
    if (varVal) {
      if (varVal.endsWith('rem')) {
        const rem = parseFloat(varVal);
        const rootFont = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        px = rem * rootFont;
      } else if (varVal.endsWith('px')) {
        px = parseFloat(varVal);
      } else {
        const num = parseFloat(varVal);
        if (!Number.isNaN(num)) px = num;
      }
    }
    if (!px) {
      const attr = this.getAttribute('scroll-offset');
      if (attr) {
        const n = parseInt(attr, 10);
        if (!Number.isNaN(n)) px = n;
      }
    }
    return px || 0;
  }

  _stopEvent(e) {
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
  }

  _onClick (e) {
    const a = e.target.closest('a[href]');
    if (!a || !this.contains(a)) return;

    if (e.defaultPrevented || e.button !== 0 || a.target === '_blank' ||
      e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }

    let url;
    try {
      url = new URL(a.getAttribute('href'), window.location.href);
    } catch {
      return;
    }

    const samePage = url.origin === window.location.origin && url.pathname === window.location.pathname;
    const hash = url.hash;

    if (!(samePage && hash)) return;

    if (hash && hash === window.location.hash) {
      this._stopEvent(e);
      this._suppressScrollOnce = true;

      this._deactivateCurrentTarget();
      this._setActiveLink(this._getBaseLink());
      this._clearHash();

      requestAnimationFrame(() => { this._suppressScrollOnce = false; });
      return;
    }

    if (samePage && hash && hash !== '#') {
      this._stopEvent(e);
      if (window.location.hash !== hash) {
        history.pushState(null, '', hash);
      }
      this._activateByHash(hash);
    }
  }

  _onHashMaybeActivate () {
    const { hash } = window.location;

    if (!hash || hash === '#') {
      this._deactivateCurrentTarget();
      this._setActiveLink(this._getBaseLink());
      return;
    }

    const link = this._findMenuLinkByHash(hash);
    if (!link) return;

    let url;
    try {
      url = new URL(link.getAttribute('href'), window.location.href);
    } catch {
      return;
    }
    const samePage = url.origin === window.location.origin && url.pathname === window.location.pathname;
    if (!samePage || url.hash !== hash) return;

    this._activateByHash(hash);
  }

  _activateByHash (hash) {
    const suppressScroll = this._suppressScrollOnce === true;

    const key = hash.slice(1);
    const target = this._findTargetElement(key);
    if (!target) return;

    if (!target.hasAttribute('data-collapsible')) {
      this._deactivateCurrentTarget();
      this._setActiveLink(this._getBaseLink());
      return;
    }

    if (this._lastTarget && this._lastTarget !== target && this._lastTarget.isConnected) {
      this._lastTarget.classList.remove(this.activeClass);
    }

    const link = this._findMenuLinkByHash(hash);
    this._setActiveLink(link);

    const wasActive = target.classList.contains(this.activeClass);
    target.classList.add(this.activeClass);
    this._lastTarget = target;

    if (!wasActive && !suppressScroll && this._isBelowViewport(target)) {
      this._scrollToWithOffset(target);
    }
  }

  _deactivateCurrentTarget () {
    if (this._lastTarget && this._lastTarget.isConnected) {
      this._lastTarget.classList.remove(this.activeClass);
    }
    this._lastTarget = null;
  }

  _clearHash () {
    const { origin, pathname, search } = window.location;
    history.pushState(null, '', origin + pathname + search);
  }

  _findTargetElement (key) {
    let el = document.getElementById(key);
    if (el) return el;
    el = document.querySelector(`[data-id="${CSS.escape(key)}"]`);
    return el || null;
  }

  _findMenuLinkByHash (hash) {
    const candidates = this.querySelectorAll('a[href]');
    for (const a of candidates) {
      try {
        const u = new URL(a.getAttribute('href'), window.location.href);
        if (u.hash === hash &&
          u.origin === window.location.origin &&
          u.pathname === window.location.pathname) {
          return a;
        }
      } catch {}
    }
    return null;
  }

  _getBaseLink () {
    const links = this.querySelectorAll('a[href]');
    for (const a of links) {
      try {
        const u = new URL(a.getAttribute('href'), window.location.href);
        const same = u.origin === window.location.origin && u.pathname === window.location.pathname;
        if (same && (!u.hash || u.hash === '#')) return a;
      } catch {}
    }
    return null;
  }

  _setActiveLink (activeLink) {
    const links = this.querySelectorAll('a[href].collection-hero-horizontal-menu__item');
    links.forEach(a => a.classList.remove(this.linkActiveClass));
    if (activeLink) activeLink.classList.add(this.linkActiveClass);
  }

  _isBelowViewport (el) {
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    return rect.top >= vh;
  }

  _scrollToWithOffset (el) {
    const offset = this._getScrollOffsetPx();
    const rect = el.getBoundingClientRect();
    const targetY = window.scrollY + rect.top - offset;
    window.scrollTo({ top: targetY, behavior: 'smooth' });
  }
}

if (!('CSS' in window) || typeof CSS.escape !== 'function') {
  window.CSS = window.CSS || {};
  CSS.escape = function (sel) {
    return String(sel).replace(/[^a-zA-Z0-9_\u00A0-\uFFFF-]/g, '\\$&');
  };
}

if (!customElements.get('collection-menu-controller')) {
  customElements.define('collection-menu-controller', CollectionMenuController);
}
